import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';
import WorkOrderCard from '../components/WorkOrderCard';
import WorkOrderComments from '../components/WorkOrderComments';
import CreateWorkOrderModal from '../components/CreateWorkOrderModal';

const INITIAL_ASSIGN_FORM = {
  assigned_to: '',
  assigned_shop_id: '',
  due_date: ''
};

const INITIAL_COMPLETE_FORM = {
  completion_notes: ''
};

const INITIAL_CLOSEOUT_FORM = {
  completion_cost: '',
  cost_category: '',
  service_mileage: '',
  vendor_name: '',
  work_performed_summary: '',
  damaging_driver_id: '',
  receipt_photo: null
};

const COST_CATEGORY_OPTIONS = [
  { value: 'routine', label: 'Routine Maintenance' },
  { value: 'breakdown', label: 'Breakdown / Mechanical Failure' },
  { value: 'damage', label: 'Driver-Caused Damage' },
  { value: 'inspection', label: 'Inspection Issue / Repair' },
];

const SECTIONS = [
  { key: 'needs_assignment', title: 'Needs Assignment', color: '#3b82f6', filter: wo => wo.status === 'open', emptyMsg: 'No work orders awaiting assignment' },
  { key: 'active', title: 'Active Work', color: '#f59e0b', filter: wo => wo.status === 'assigned' || wo.status === 'in_progress', emptyMsg: 'No active work orders' },
  { key: 'review', title: 'Awaiting Review', color: '#10b981', filter: wo => wo.status === 'completed', emptyMsg: 'No work orders awaiting review' }
];

export default function WorkOrderQueuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, can, isAtLeastAdmin } = useAuth();
  const isAdmin = isAtLeastAdmin;
  const isDriver = (user.team_name || '') === 'Driver Operations' || user.role === 'driver';

  // Data state
  const [workOrders, setWorkOrders] = useState([]);
  const [stats, setStats] = useState({ open: 0, assigned: 0, in_progress: 0, completed: 0 });
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCloseoutModal, setShowCloseoutModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  // Form state
  const [assignForm, setAssignForm] = useState(INITIAL_ASSIGN_FORM);
  const [completeForm, setCompleteForm] = useState(INITIAL_COMPLETE_FORM);
  const [closeoutForm, setCloseoutForm] = useState(INITIAL_CLOSEOUT_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Collapsible sections
  const [sectionsOpen, setSectionsOpen] = useState({ needs_assignment: true, active: true, review: true });

  // Card highlighted via ?id= from a bucket row click
  const [highlightId, setHighlightId] = useState(null);

  // Honor query params from the bucket page: ?create=1 opens the create
  // modal directly, ?id=N scrolls to that card once data loads.
  useEffect(() => {
    if (searchParams.get('create') === '1' && isAdmin) {
      setShowCreateModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    const id = searchParams.get('id');
    if (id) setHighlightId(Number(id));
  }, []);

  // Scroll the highlighted card into view once work orders are on screen
  useEffect(() => {
    if (loading || !highlightId) return;
    const el = document.getElementById(`wo-card-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loading, highlightId]);

  const toggleSection = (key) => {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Data loaders
  const loadWorkOrders = useCallback(async () => {
    try {
      const data = await api.getWorkOrders();
      // For drivers, only show their assigned work orders
      if (isDriver) {
        setWorkOrders(data.filter(wo => wo.assigned_to === user.id));
      } else {
        setWorkOrders(data);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load work orders:', err);
      setError('Failed to load work orders');
    }
    setLoading(false);
  }, [isDriver, user.id]);

  const loadStats = useCallback(async () => {
    try {
      const data = await api.getWorkOrderStats();
      if (data) setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  // Initial load + polling
  useEffect(() => {
    loadWorkOrders();
    loadStats();
    api.getShops().then(setShops).catch(console.error);
    api.getUsers().then(setUsers).catch(console.error);

    const woInterval = setInterval(loadWorkOrders, 10000);
    const statsInterval = setInterval(loadStats, 10000);
    return () => {
      clearInterval(woInterval);
      clearInterval(statsInterval);
    };
  }, [loadWorkOrders, loadStats]);

  // Reload helper
  const reload = () => {
    loadWorkOrders();
    loadStats();
  };

  // Assignable users: maintenance team or driver team, active only
  const assignableUsers = users.filter(u =>
    (u.team_name === 'Truck Maintenance' || u.team_name === 'Driver Operations' || u.role === 'maintenance' || u.role === 'driver') && u.is_active !== 0
  );

  // --- Action Handlers ---

  function openAssignModal(workOrder) {
    setSelectedWorkOrder(workOrder);
    setAssignForm({
      assigned_to: workOrder.assigned_to || '',
      assigned_shop_id: workOrder.assigned_shop_id || '',
      due_date: workOrder.due_date || ''
    });
    setShowAssignModal(true);
  }

  async function handleAssign(e) {
    e.preventDefault();
    if (!assignForm.assigned_to || !assignForm.assigned_shop_id) return;
    setSubmitting(true);
    try {
      await api.updateWorkOrder(selectedWorkOrder.id, {
        assigned_to: Number(assignForm.assigned_to),
        assigned_shop_id: Number(assignForm.assigned_shop_id),
        due_date: assignForm.due_date || null
      });
      setShowAssignModal(false);
      setSelectedWorkOrder(null);
      reload();
    } catch (err) {
      alert(err.message || 'Failed to assign work order');
    }
    setSubmitting(false);
  }

  async function handleClaim(id) {
    try {
      await api.claimWorkOrder(id);
      reload();
    } catch (err) {
      alert(err.message || 'Failed to start work');
    }
  }

  function openCompleteModal(workOrder) {
    setSelectedWorkOrder(workOrder);
    setCompleteForm(INITIAL_COMPLETE_FORM);
    setShowCompleteModal(true);
  }

  async function handleComplete(e) {
    e.preventDefault();
    if (!completeForm.completion_notes.trim()) return;
    setSubmitting(true);
    try {
      // Driver/tech only confirms work was done. Cost & receipt move to admin close-out.
      await api.completeWorkOrder(selectedWorkOrder.id, {
        completion_notes: completeForm.completion_notes.trim()
      });
      setShowCompleteModal(false);
      setSelectedWorkOrder(null);
      reload();
    } catch (err) {
      alert(err.message || 'Failed to complete work order');
    }
    setSubmitting(false);
  }

  function openCloseoutModal(workOrder) {
    setSelectedWorkOrder(workOrder);
    setCloseoutForm(INITIAL_CLOSEOUT_FORM);
    setShowCloseoutModal(true);
  }

  async function handleCloseout(e) {
    e.preventDefault();
    if (!selectedWorkOrder) return;
    if (!closeoutForm.cost_category) return alert('Pick a cost category.');
    if (closeoutForm.completion_cost === '' || Number(closeoutForm.completion_cost) < 0) return alert('Enter a valid cost.');
    if (closeoutForm.service_mileage === '' || Number(closeoutForm.service_mileage) < 0) return alert('Enter the service mileage.');
    if (!closeoutForm.work_performed_summary.trim()) return alert('Enter a work performed summary.');
    if (closeoutForm.cost_category === 'damage' && !closeoutForm.damaging_driver_id) return alert('Pick the driver responsible for the damage.');

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('completion_cost', String(closeoutForm.completion_cost));
      formData.append('cost_category', closeoutForm.cost_category);
      formData.append('service_mileage', String(closeoutForm.service_mileage));
      formData.append('work_performed_summary', closeoutForm.work_performed_summary.trim());
      if (closeoutForm.vendor_name.trim()) formData.append('vendor_name', closeoutForm.vendor_name.trim());
      if (closeoutForm.damaging_driver_id) formData.append('damaging_driver_id', String(closeoutForm.damaging_driver_id));
      if (closeoutForm.receipt_photo) formData.append('receipt_photo', closeoutForm.receipt_photo);
      await api.closeWorkOrder(selectedWorkOrder.id, formData);
      setShowCloseoutModal(false);
      setSelectedWorkOrder(null);
      reload();
    } catch (err) {
      alert(err.message || 'Failed to close work order');
    }
    setSubmitting(false);
  }

  if (loading) {
    return <div style={{ padding: '24px' }}><div className="loading">Loading...</div></div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div className="maint-types__header">
        <h1>{isDriver ? 'My Work Orders' : 'Maintenance Queue'}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* The Queue is its own bucket — back goes to the Overview grid */}
          <button className="btn btn-outline" onClick={() => navigate('/maintenance')}>
            ← Overview
          </button>
          {isAdmin && (
            <button className="btn btn-gold" onClick={() => setShowCreateModal(true)}>
              + Create Work Order
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger)', padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '14px', fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* KPI Stats Row */}
      <div className="kpi-grid">
        <div className="kpi-card" style={{ '--kpi-accent': '#3b82f6' }}>
          <div className="kpi-value">{stats.open}</div>
          <div className="kpi-label">Open</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#f59e0b' }}>
          <div className="kpi-value">{stats.assigned}</div>
          <div className="kpi-label">Assigned</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#8b5cf6' }}>
          <div className="kpi-value">{stats.in_progress}</div>
          <div className="kpi-label">In Progress</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#10b981' }}>
          <div className="kpi-value">{stats.completed}</div>
          <div className="kpi-label">Completed</div>
        </div>
      </div>

      {/* Driver empty state */}
      {isDriver && workOrders.length === 0 && (
        <div className="empty-region" style={{ marginTop: '24px' }}>
          No work orders assigned to you
        </div>
      )}

      {/* Queue Sections — same pattern as RegionSection */}
      {SECTIONS.map(section => {
        const sectionOrders = workOrders.filter(section.filter);
        const isOpen = sectionsOpen[section.key];

        return (
          <div key={section.key} className="region-section">
            <div className="region-header" onClick={() => toggleSection(section.key)}>
              <div className="region-title">
                <span className="region-dot" style={{ background: section.color }} />
                <h3>{section.title}</h3>
                <span className="region-count">{sectionOrders.length}</span>
              </div>
              <span className={`collapse-icon ${!isOpen ? 'rotated' : ''}`}>&#9660;</span>
            </div>
            {isOpen && (
              <div className="region-body">
                {sectionOrders.length === 0 ? (
                  <div className="empty-region">{section.emptyMsg}</div>
                ) : (
                  <div className="calls-grid">
                    {sectionOrders.map(wo => (
                      <div
                        key={wo.id}
                        id={`wo-card-${wo.id}`}
                        style={wo.id === highlightId ? { outline: '2px solid var(--gold, #d4af37)', borderRadius: 'var(--radius-md)' } : undefined}
                      >
                        <WorkOrderCard
                          workOrder={wo}
                          user={user}
                          can={can}
                          onAssign={openAssignModal}
                          onClaim={handleClaim}
                          onComplete={openCompleteModal}
                          onClose={openCloseoutModal}
                          onComment={(wo) => { setSelectedWorkOrder(wo); setShowCommentModal(true); }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Create Work Order Modal (shared with the Work Orders bucket) */}
      {showCreateModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); reload(); }}
        />
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedWorkOrder && (
        <Modal title="Assign Work Order" onClose={() => { setShowAssignModal(false); setSelectedWorkOrder(null); }}>
          <div style={{ background: 'var(--bg-subtle)', padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <strong style={{ fontSize: '15px' }}>{selectedWorkOrder.title}</strong>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Truck: {selectedWorkOrder.unit_number}</span>
          </div>
          <form onSubmit={handleAssign}>
            <div className="form-group">
              <label>Assign To *</label>
              <select
                value={assignForm.assigned_to}
                onChange={e => setAssignForm(f => ({ ...f, assigned_to: e.target.value }))}
                required
              >
                <option value="">Select person...</option>
                {assignableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}{u.position_name ? ` — ${u.position_name}` : ''}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Shop *</label>
              <select
                value={assignForm.assigned_shop_id}
                onChange={e => setAssignForm(f => ({ ...f, assigned_shop_id: e.target.value }))}
                required
              >
                <option value="">Select shop...</option>
                {shops.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={assignForm.due_date}
                onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-muted" onClick={() => { setShowAssignModal(false); setSelectedWorkOrder(null); }}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={submitting}>
                {submitting ? 'Saving...' : 'Assign'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Complete Modal */}
      {showCompleteModal && selectedWorkOrder && (
        <Modal title="Complete Work Order" onClose={() => { setShowCompleteModal(false); setSelectedWorkOrder(null); }}>
          <div style={{ background: 'var(--bg-subtle)', padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <strong style={{ fontSize: '15px' }}>{selectedWorkOrder.title}</strong>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Truck: {selectedWorkOrder.unit_number}</span>
          </div>
          <form onSubmit={handleComplete}>
            <div className="form-group">
              <label>Completion Notes *</label>
              <textarea
                value={completeForm.completion_notes}
                onChange={e => setCompleteForm(f => ({ ...f, completion_notes: e.target.value }))}
                placeholder="Describe the work performed..."
                rows={4}
                required
              />
            </div>

            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              Cost, vendor, mileage, and receipt are captured by an admin during close-out.
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-muted" onClick={() => { setShowCompleteModal(false); setSelectedWorkOrder(null); }}>Cancel</button>
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Mark Completed'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Close-Out Modal (Admin) */}
      {showCloseoutModal && selectedWorkOrder && (
        <Modal title="Close-Out Work Order" onClose={() => { setShowCloseoutModal(false); setSelectedWorkOrder(null); }}>
          <div style={{ background: 'var(--bg-subtle)', padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <strong style={{ fontSize: '15px' }}>{selectedWorkOrder.title}</strong>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Truck: {selectedWorkOrder.unit_number}</span>
          </div>
          <form onSubmit={handleCloseout}>
            <div className="form-group">
              <label>Cost Category *</label>
              <select
                value={closeoutForm.cost_category}
                onChange={e => setCloseoutForm(f => ({ ...f, cost_category: e.target.value, damaging_driver_id: e.target.value === 'damage' ? f.damaging_driver_id : '' }))}
                required
              >
                <option value="">Select category...</option>
                {COST_CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {closeoutForm.cost_category === 'damage' && (
              <div className="form-group">
                <label>Driver Responsible *</label>
                <select
                  value={closeoutForm.damaging_driver_id}
                  onChange={e => setCloseoutForm(f => ({ ...f, damaging_driver_id: e.target.value }))}
                  required
                >
                  <option value="">Select driver...</option>
                  {users
                    .filter(u => (u.team_name === 'Driver Operations' || u.role === 'driver') && u.is_active !== 0)
                    .map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)
                  }
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Total Cost *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={closeoutForm.completion_cost}
                onChange={e => setCloseoutForm(f => ({ ...f, completion_cost: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>

            <div className="form-group">
              <label>Service Mileage *</label>
              <input
                type="number"
                step="1"
                min="0"
                value={closeoutForm.service_mileage}
                onChange={e => setCloseoutForm(f => ({ ...f, service_mileage: e.target.value }))}
                placeholder="e.g., 236500"
                required
              />
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>The truck's mileage when this service was performed. Becomes the new baseline for the next interval.</div>
            </div>

            <div className="form-group">
              <label>Vendor (if not the assigned shop)</label>
              <input
                type="text"
                value={closeoutForm.vendor_name}
                onChange={e => setCloseoutForm(f => ({ ...f, vendor_name: e.target.value }))}
                placeholder="e.g., A&T Wrecker Repair"
              />
            </div>

            <div className="form-group">
              <label>Work Performed Summary *</label>
              <textarea
                value={closeoutForm.work_performed_summary}
                onChange={e => setCloseoutForm(f => ({ ...f, work_performed_summary: e.target.value }))}
                placeholder="What was actually done? (parts, labor, findings)"
                rows={4}
                required
              />
            </div>

            <div className="form-group">
              <label>Receipt / Invoice (image or PDF)</label>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={e => setCloseoutForm(f => ({ ...f, receipt_photo: e.target.files[0] || null }))}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-muted" onClick={() => { setShowCloseoutModal(false); setSelectedWorkOrder(null); }}>Cancel</button>
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? 'Closing...' : 'Close-Out & Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Comments Modal */}
      {showCommentModal && selectedWorkOrder && (
        <Modal title={`Comments — ${selectedWorkOrder.title}`} onClose={() => setShowCommentModal(false)}>
          <div style={{ minHeight: '300px' }}>
            <WorkOrderComments workOrderId={selectedWorkOrder.id} />
          </div>
        </Modal>
      )}
    </div>
  );
}
